import { CSSResult, html, LitElement, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { repeat } from 'lit/directives/repeat.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import styles from './wishlist-brands.scss?inline';
import { Messages, WishlistBrandListItem } from '../../../@types/wishlist.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { brandListApi } from '../../../scripts/service/brand-list-api.js';
import { brandsContext } from '../../../scripts/context/wishlistContext.js';
import { BrandsState } from '../../../scripts/context/wishlist-state.js';
import {
  trackBrandlistAllBrandsButtonClick,
  trackViewBrandlist,
} from '../../../scripts/service/cube-tracking-layer.js';
import heartIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_heart.svg?element';
import checkIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_check.svg?element';
import { hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';
import '../wishlist-brand-tile/wishlist-brand-tile.js';
import '../wishlist-alert/wishlist-alert.js';

@customElement('ents-wishlist-brands')
export class WishlistBrandsElement extends LitElement {
  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: brandsContext, subscribe: true })
  private brandsState!: BrandsState;

  @property({ type: Boolean, attribute: 'logged-in' }) private loggedIn: boolean = false;

  @state() private loading: boolean = true;
  @state() private loadFailed: boolean = false;

  private tilesTrackingObserver: IntersectionObserver | null = null;

  private viewBrandlistTracked = false;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  override connectedCallback() {
    super.connectedCallback();
    this.tilesTrackingObserver = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => (entry.target as WishlistBrandTileElement).trackViewDebounced(entry.isIntersecting)),
      { threshold: 0.5 },
    );
    void this.load();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.tilesTrackingObserver?.disconnect();
    this.tilesTrackingObserver = null;
  }

  private async load() {
    // brands are cached in the app-level brandsState, so the API is only hit on the first tab visit
    if (this.brandsState.loaded) {
      this.loading = false;
      this.trackViewBrandlistOnce();
      return;
    }
    this.loading = true;
    this.loadFailed = false;
    try {
      // fetched first, then read this.brandsState fresh - it may have been reassigned to a new
      // object (e.g. by an optimistic add() from the recommendations slider) while this awaited,
      // and `this.brandsState.mergeFetched(await ...)` would resolve the (now stale) receiver
      // before the await even starts, silently merging against a pre-add entities snapshot
      const fetched = await brandListApi.listFollowed(this.wishlistAppConfig.brandsApiEndpoint);
      this.brandsState.mergeFetched(fetched);
      this.trackViewBrandlistOnce();
    } catch (e) {
      console.error('failed to load followed brands', e);
      this.loadFailed = true;
    } finally {
      this.loading = false;
    }
  }

  private trackViewBrandlistOnce() {
    if (this.viewBrandlistTracked) return;
    this.viewBrandlistTracked = true;
    trackViewBrandlist(this.visibleBrands);
  }

  private get visibleBrands(): WishlistBrandListItem[] {
    return this.brandsState.getVisibleBrands();
  }

  protected updated() {
    // expose the loading state to assistive technology without needing a translated label
    this.setAttribute('aria-busy', String(this.loading));
  }

  render() {
    if (this.loading) return this.renderLoading();
    if (this.loadFailed) return this.renderError();
    if (this.visibleBrands.length === 0) return this.renderEmpty();
    const totalBrands = this.visibleBrands.length;
    return html`
      ${this.renderBenefitsHint()}
      <ul class="ents-wishlist-brands-grid" data-test-ents-wishlist-brands-grid>
        ${repeat(
          this.visibleBrands,
          (b) => b.code,
          (b, index) => this.renderTile(b, index + 1, totalBrands),
        )}
        ${this.renderAzTile()}
      </ul>
    `;
  }

  private renderBenefitsHint() {
    return html`
      <wishlist-alert
        class="ents-wishlist-brands-hint"
        local-storage-id="ents-wishlist-brands-hint"
        data-test-ents-wishlist-brands-hint
      >
        <span slot="title" class="ents-wishlist-brands-hint__headline">
          ${this.messages['ents_wishlist_brands_hint_headline']}
        </span>
        <ul class="ents-wishlist-brands-hint__benefits">
          <li>${this.messages['ents_wishlist_brands_hint_benefit_1']}</li>
          <li>${this.messages['ents_wishlist_brands_hint_benefit_2']}</li>
          <li>${this.messages['ents_wishlist_brands_hint_benefit_3']}</li>
        </ul>
      </wishlist-alert>
    `;
  }

  private renderTile(brand: WishlistBrandListItem, position: number, totalBrands: number) {
    return html`
      <li class="ents-wishlist-brand-tile" data-test-ents-wishlist-brand-tile="${brand.code}">
        <ents-wishlist-brand-tile
          .brand="${brand}"
          .position="${position}"
          .totalBrands="${totalBrands}"
          .loggedIn="${this.loggedIn}"
          .trackingObserver="${this.tilesTrackingObserver}"
        ></ents-wishlist-brand-tile>
      </li>
    `;
  }

  private renderAzTile() {
    return html`
      <li class="ents-wishlist-brand-tile ents-wishlist-brand-tile--link" data-test-ents-wishlist-brands-az-tile>
        <a
          href="${this.wishlistAppConfig.brandsUrl}"
          class="ents-wishlist-brand-tile__az-link bdk-link-primary"
          @click="${() => trackBrandlistAllBrandsButtonClick(false)}"
        >
          ${this.messages['ents_wishlist_brands_az_link_text']}
        </a>
      </li>
    `;
  }

  private renderEmpty() {
    const benefits = [
      this.messages['ents_wishlist_brands_empty_benefit_1'],
      this.messages['ents_wishlist_brands_empty_benefit_2'],
      this.messages['ents_wishlist_brands_empty_benefit_3'],
    ];
    return html`
      <div class="ents-wishlist-brands-empty" data-test-ents-wishlist-brands-empty>
        <div class="ents-wishlist-brands-empty__icon">${hideIconFromAssistiveTechnology(heartIcon())}</div>
        <div class="ents-wishlist-brands-empty__text">
          <h2 class="ents-wishlist-brands-empty__headline ents-copy-bold">
            ${this.messages['ents_wishlist_brands_empty_headline']}<br />${
              this.messages['ents_wishlist_brands_empty_subheadline']
            }
          </h2>
          <ul class="ents-wishlist-brands-empty__benefits">
            ${benefits.map((benefit) => this.renderBenefit(benefit))}
          </ul>
        </div>
        <a
          class="bdk-button"
          type="button"
          variant="outline"
          size="medium"
          href="${this.wishlistAppConfig.brandsUrl}"
          @click="${() => trackBrandlistAllBrandsButtonClick(true)}"
        >
          ${this.messages['ents_wishlist_brands_az_link_text']}
        </a>
      </div>
    `;
  }

  private renderLoading() {
    return html`
      <div class="ents-wishlist-brands-loading" data-test-ents-wishlist-brands-loading>
        <span class="ents-wishlist-brands-loading__spinner" aria-hidden="true"></span>
      </div>
    `;
  }

  private renderError() {
    return html`
      <div class="ents-wishlist-brands-error" data-test-ents-wishlist-brands-error>
        <div class="ents-wishlist-brands-error__icon">${hideIconFromAssistiveTechnology(heartIcon())}</div>
        <p class="ents-wishlist-brands-error__text ents-copy">${this.messages['ents_wishlist_error_box_text']}</p>
      </div>
    `;
  }

  private renderBenefit(text?: string) {
    return html`
      <li class="ents-wishlist-brands-empty__benefit ents-copy">
        <span class="ents-wishlist-brands-empty__benefit-icon">${hideIconFromAssistiveTechnology(checkIcon())}</span>
        <span>${text}</span>
      </li>
    `;
  }
}
